/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { Avatar, Tooltip } from 'antd'
import colors from '@sportninja/common/constants/appColors'
import {
  describeOfficial,
  type TeamHistoryOfficialView,
} from '@sportninja/common/features/registration/teamHistory'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import OfficialAvatar from './OfficialAvatar'

/** More than this collapses into a "+N" bubble. */
const MAX_VISIBLE = 3

/**
 * The stack renders inside a Select popup, which the global stylesheet lifts to
 * `z-index: 999999` (`.ant-select-dropdown` in `index*.scss`). antd's default
 * tooltip layer (~1070) would open underneath it. The popup clips overflow, so
 * the tooltip can't be portalled into it either — it has to sit one layer above.
 */
const TOOLTIP_Z_INDEX = 999999 + 1

interface Props {
  officials: TeamHistoryOfficialView[]
  treasurerLabel: string
}

/**
 * The officials on a team's last roster, as overlapping avatars. Each names
 * the person, role and treasurer flag on hover — so two teams with the same
 * name can be told apart by who runs them.
 */
const OfficialAvatarStack = ({ officials, treasurerLabel }: Props) => (
  <Avatar.Group
    maxCount={MAX_VISIBLE}
    size={24}
    maxStyle={overflowBubble}
    css={stackStyle}
  >
    {officials.map((official) => (
      <Tooltip
        key={official.id}
        title={describeOfficial(official, capitalizeWords(treasurerLabel))}
        zIndex={TOOLTIP_Z_INDEX}
      >
        <OfficialAvatar official={official} />
      </Tooltip>
    ))}
  </Avatar.Group>
)

export default OfficialAvatarStack

/* Stays vertically centred in the row even though the row top-aligns. */
const stackStyle = css`
  flex-shrink: 0;
  align-self: center;
`

const overflowBubble = {
  backgroundColor: colors.NEUTRAL_600,
  color: colors.SECONDARY_50,
}
